import React from 'react';
import Dropzone, {
    Accept,
    FileRejection,
    DropzoneInputProps,
} from 'react-dropzone';
import _isEqual from 'lodash/isEqual';
import { useSelector } from 'react-redux';

import {
    AddCircleIcon,
    ExclamationIcon,
    FileUploadDropzoneIcon,
} from '../icons/icons';
import {
    IDropZoneErrorMessages,
    DropZoneType,
    acceptPdf,
} from './uploadFileConstants';
import { RootState } from '../../../store';
import { getFileCloneWithFixedMissingMimeType } from '../../../utils/file';
import FileItem from '../FileItem/FileItem';

import './UploadFile.css';

export interface IUploadFileProps {
    label: string | null;
    file: File | null;
    fileUrl: string;
    accept: Accept;
    onRemove: (fileId: number) => void;
    onDrop: (
        acceptedFiles: File[],
        fileRejections: FileRejection[],
        event: string
    ) => void;
    fieldName: string;
    pictureClass: string;
    type: DropZoneType;
    maxSize: number;
    multiple?: boolean;
    files?: Map<number, File>;
    isRequired?: boolean;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onMainFileCheck?: (fileKey: number) => void;
    onPresentationFileCheck?: (fileKey: number) => void;
    checkedMainFileKey?: number;
    checkedPresentationFileKey?: number;
    showFileList?: boolean;
    degreeType?: boolean;
    hidePresentationFile?: boolean;
}

export const UploadFile = (props: IUploadFileProps) => {
    const localization = useSelector((state: RootState) => state.localization);
    const uploadFileLocalization =
        localization.localizationStrings.susy.settings.UploadFile;
    const renderErrorMessage = (message: string, key: number) => (
        <div className='upload-err-message' key={key}>
            <ExclamationIcon />
            <p>{message}</p>
        </div>
    );
    const getErrorMessages = (type: DropZoneType): IDropZoneErrorMessages => {
        if (type === DropZoneType.project) {
            return {
                'file-invalid-type':
                    uploadFileLocalization.invalidFileTypeProjectError,
                'file-too-large': uploadFileLocalization.fileLargeProjectError,
            };
        }

        return {
            'file-invalid-type': uploadFileLocalization.invalidFileTypeError,
            'file-too-large': uploadFileLocalization.fileLargeError,
        };
    };

    const checkPdfAcceptance = () => {
        return _isEqual(props.accept, acceptPdf);
    };

    const getDropzoneClassName = () => {
        const res = ['susy-dropzone', 'mb-4'];
        if (props.fieldName === 'profilePicture') {
            res.push(props.pictureClass);
        }

        return res.join(' ');
    };

    const renderFileRejections = (fileRejections: FileRejection[]) => {
        const errorMessages: IDropZoneErrorMessages = getErrorMessages(
            props.type
        );
        return (
            <div>
                {fileRejections.map(({ file, errors }) => {
                    return errors.map((err, index) =>
                        renderErrorMessage(
                            `${file.name} - ${errorMessages[err.code]}`,
                            index
                        )
                    );
                })}
            </div>
        );
    };

    const renderUploadLabel = () => {
        return props.label == null || props.label === '' ? null : (
            <label className='upload-label'>{props.label}</label>
        );
    };

    const renderUploadInput = (inputProps: DropzoneInputProps) => {
        const renderSupervisorSignUpDegreeTypeUploadIcon = () => {
            return (
                <>
                    <AddCircleIcon />
                    <div className='mt-2'>
                        <label>{uploadFileLocalization.selectDegree}</label>
                    </div>
                    <div>
                        {uploadFileLocalization.DropOrClickInfoPart1}
                        <div className='pseudo-anchor'>
                            {' '}
                            {uploadFileLocalization.DropOrClickInfoPart2}
                        </div>
                    </div>
                </>
            );
        };

        const renderRegularUploadIcon = () => {
            return (
                <div>
                    <FileUploadDropzoneIcon />
                    <div className='upload-form-dd-span mt-3'>
                        {uploadFileLocalization.DropOrClickInfoPart1}
                        <span className='pseudo-anchor'>
                            {uploadFileLocalization.DropOrClickInfoPart2}
                        </span>
                    </div>
                </div>
            );
        };

        return (
            <>
                <input
                    name='document'
                    data-testid='dropzone-input-id'
                    {...inputProps}
                    required={props.isRequired}
                />
                {props.degreeType
                    ? renderSupervisorSignUpDegreeTypeUploadIcon()
                    : renderRegularUploadIcon()}
            </>
        );
    };

    const getDropZoneClasses = (
        isDragActive: boolean,
        isDragReject: boolean
    ) => {
        return `dz-default dz-message dz-small ${
            isDragActive
                ? isDragReject
                    ? 'is-drag-reject'
                    : 'is-drag-accepted'
                : ''
        } ${props.degreeType ? 'degree-type-padding' : ''}`;
    };

    const renderUploadedFiles = (files: Map<number, File>) => {
        return (
            <div data-testid='files-items-list' className='list-group'>
                {Array.from(files, ([fileKey, value]) => ({
                    fileKey,
                    value,
                })).map((f, index) => {
                    return (
                        <FileItem
                            idPrefix='existing-files-'
                            index={index}
                            name={f.value.name}
                            size={f.value.size}
                            hidePresentationFile={props.hidePresentationFile}
                            toggleMainFile={
                                props.onMainFileCheck
                                    ? () => {
                                          props.onMainFileCheck?.(f.fileKey);
                                      }
                                    : undefined
                            }
                            togglePresentationFile={
                                props.onPresentationFileCheck
                                    ? () => {
                                          props.onPresentationFileCheck?.(
                                              f.fileKey
                                          );
                                      }
                                    : undefined
                            }
                            isMainFile={props.checkedMainFileKey === f.fileKey}
                            isPresentationFile={
                                props.checkedPresentationFileKey === f.fileKey
                            }
                            key={index}
                            onRemoveExistingDocs={() => {
                                props.onRemove(f.fileKey);
                            }}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <div className='susy-upload-file-box' onBlur={props.onBlur}>
            {renderUploadLabel()}
            {!props.fileUrl && (
                <div className={getDropzoneClassName()}>
                    <Dropzone
                        onDrop={(files, rejected) => {
                            const filesCloneWithFixedMissingMimeType =
                                files.map((file) =>
                                    getFileCloneWithFixedMissingMimeType(file)
                                );
                            props.onDrop(
                                filesCloneWithFixedMissingMimeType,
                                rejected,
                                props.fieldName
                            );
                        }}
                        accept={props.accept}
                        multiple={props.multiple}
                        maxSize={props.maxSize}
                    >
                        {({
                            getRootProps,
                            getInputProps,
                            isDragActive,
                            isDragReject,
                            fileRejections,
                        }) => (
                            <React.Fragment>
                                <div
                                    data-testid='dropzone-uploader'
                                    className='dropzone dropzone-single dz-clickable'
                                >
                                    <div
                                        className={getDropZoneClasses(
                                            isDragActive,
                                            isDragReject
                                        )}
                                        {...getRootProps()}
                                    >
                                        {renderUploadInput({
                                            ...getInputProps(),
                                        })}
                                    </div>
                                </div>
                                {fileRejections.length > 0 &&
                                    renderFileRejections(fileRejections as any)}
                            </React.Fragment>
                        )}
                    </Dropzone>
                </div>
            )}
            {props.file && checkPdfAcceptance() && (
                <div>
                    <FileItem
                        index={0}
                        name={props.file.name}
                        size={props.file.size}
                        hidePresentationFile={props.hidePresentationFile}
                        onRemoveExistingDocs={() => {
                            props.onRemove(Number(props.fieldName));
                        }}
                    />
                </div>
            )}
            {props.files &&
                (props.multiple || props.showFileList) &&
                renderUploadedFiles(props.files)}
        </div>
    );
};
